import { useCallback } from "react";
import { ReportSummaryItemApi } from "../api/Reports";
import SecurityLockIcon from "../shared-components/icons/SecurityLockIcon";
import { classNames } from "../utils";

import { useReportUnlock } from "./useReportUnlock";
import { isReportLockedByOther } from "./utils";
import { isActorUuidMatch } from "../api/Users";
import { useActorInfoQuery } from "../api/UsersApi";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

type Props = {
  report: ReportSummaryItemApi;
  onClick: (report: ReportSummaryItemApi) => void;
  disabled?: boolean;
  forceLocked?: boolean;
  onUnlockSuccess?: () => void;
  /** When false, hide the rerun arrow (e.g. amendment-only lists). Default true. */
  showRerunIndicator?: boolean;
};

const ReportDetailLink = ({
  report,
  onClick,
  disabled = false,
  forceLocked = false,
  onUnlockSuccess,
  showRerunIndicator = true,
}: Props) => {
  const {
    performUnlock,
    isLoading: loadingUnlockReport,
    modal: modalUnlock,
  } = useReportUnlock();
  const { data: actorInfo } = useActorInfoQuery(true);
  const actorUuid = actorInfo?.actor_uuid;
  // Mirror the backend rule and ReportNavigationModal: a lock held by the
  // current actor isn't shown as locked, even after a lock failure we already
  // hold.
  const showLockIcon =
    isReportLockedByOther(report, actorUuid) ||
    (forceLocked && !isActorUuidMatch(actorUuid, report.locked_by));

  const unlockReport = useCallback(() => {
    if (disabled) return;

    const title = `Unlock Report`;
    const message = `By doing this you will manually unlock.`;
    performUnlock(
      report.workflow_run_uuid,
      title,
      message,
      () => {
        onUnlockSuccess?.();
      },
      true
    );
  }, [performUnlock, report, disabled, onUnlockSuccess]);

  return (
    <>
      {showLockIcon && (
        <div className="w-4 justify-self-start" data-testid="report-lock-icon">
          <SecurityLockIcon
            width={"1rem"}
            height={"1rem"}
            onClick={unlockReport}
            className={"cursor-pointer"}
          />
        </div>
      )}
      <div title={report.full_name}>
        <span
          onClick={() => {
            onClick?.(report);
          }}
          className={classNames(
            "w-24",
            disabled ? "" : "font-semibold underline cursor-pointer"
          )}
        >
          {report.full_name}
        </span>
        <small className="block font-normal">{report.company_name}</small>
      </div>
      {showRerunIndicator &&
        (report.step_payload?.rerun_from_parent_uuid || report.has_rerun) && (
          <div>
            <ArrowPathIcon
              className="w-5 ml-2 text-blue-600"
              title="Rerun report"
            />
          </div>
        )}
      {modalUnlock}
      {loadingUnlockReport && <LoadingSpinner />}
    </>
  );
};

export default ReportDetailLink;
